import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllTotalPointsReportList } from 'app/api/report';
import { startExportPoll, triggerDownload } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const useOverAllPointsReport = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const navigate = useNavigate();
  const [values, setValues] = useState({
    mallName: '',
    firstBillUploadDateStart: '',
    firstBillUploadDateEnd: '',
    membershipType: '',
  });
  const [totalNumber, setTotalNumber] = useState(0);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const currentPage = 1;
  const dataPerPage = 10;
  const [isFiltering, setIsFiltering] = useState(false); // New state to track filtering
  const downloadInProgressRef = useRef(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const sectionObj = {
    sectionTitle: 'Total User Data Report',
    searchObj: {
      keys: ['invoice_number', 'shopping_amount', 'storeName', 'phoneNumber'],
      placeholder: 'Search  Name, Mall, Contact No.',
      searchType: 'server',
      handleSearch: () => {},
    },
    buttons: [
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: 'Filter',
        handleBtnClick: () => setIsFilterOpen(!isFilterOpen),
      },
    ],
    downloadObj: {
      excelClick: (e) => {
        handleDownloadexcel(e);
      },
      csvClick: (e) => {
        handleDownloadcsv(e);
      },

      pdfClick: () => () => {},
      inProgress: downloadInProgress,
    },
  };
  const fetchReportData = useCallback(
    async (page, search = '', filters = {}) => {
      setPageLoading(true);
      try {
        let requestParams = {
          page,
          limit: dataPerPage,
          search,
          mallName: filters.mallName,
          firstBillUploadDateStart: filters.firstBillUploadDateStart,
          firstBillUploadDateEnd: filters.firstBillUploadDateEnd,
          membershipType: filters.membershipType,
        };
        const response = await getAllTotalPointsReportList(requestParams);
        if (response?.responseCode === 200) {
          const dataArr = response?.data?.data || [];
          const newArray = dataArr.map((item) => ({
            ...item,
          }));
          setData(newArray);
          setFilteredData(newArray);
          const totalCount = response?.data?.count || 0;
          const tempcount = Math.ceil(totalCount / dataPerPage);
          setTotalNumber(totalCount);
          setPageCount(tempcount);
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage]
  );

  useEffect(() => {
    if (isFiltering) {
      handleFiltering();
      fetchReportData(1, '', values);
      setIsFiltering(false);
    }
  }, [isFiltering, data, values, filteredData]);

  function handleFiltering() {
    const filtered = data.filter((item) => {
      const isNameMatching =
        !values.name ||
        item?.name?.toLowerCase().includes(values.name.toLowerCase());
      const isMallMatching =
        !values.mallName ||
        item?.mallName?.toLowerCase().includes(values.mallName.toLowerCase());
      const isPhoneMatching =
        !values.phone || item?.phone?.includes(values.phone);

      let isMembershipTypeMatching =
        !values.membershipType ||
        item?.membershipType
          ?.toLowerCase()
          .includes(values.membershipType.toLowerCase());

      let isVoucherCollectedDateStartMatching =
        !values.firstBillUploadDateStart ||
        new Date(item?.firstBillUploadDate) >=
          new Date(values.firstBillUploadDateStart);

      let isVoucherCollectedDateEndMatching =
        !values.firstBillUploadDateEnd ||
        new Date(item?.firstBillUploadDate) <=
          new Date(values.firstBillUploadDateEnd);

      return (
        isNameMatching &&
        isMallMatching &&
        isPhoneMatching &&
        isMembershipTypeMatching &&
        isVoucherCollectedDateStartMatching &&
        isVoucherCollectedDateEndMatching
      );
    });

    setFilteredData(filtered);
  }
  function handleSubmit(event) {
    event.preventDefault();
    setIsFiltering(true);
  }
  function resetfilterModel() {
    setValues({
      mallName: '',
      firstBillUploadDateStart: '',
      firstBillUploadDateEnd: '',
      membershipType: '',
    });
    setFilteredData([]);
    setIsFiltering(true);
  }
  function handleFilterCancel() {
    setIsFilterOpen(false);
  }
  // Async export: returns a jobId, then polls /export/status/:jobId. We DON'T
  // block the UI with pageLoading — the Toast is enough feedback, and forcing
  // a full-page loader prevents the user from working while the export runs
  // (which can take minutes for large datasets).
  async function startDownload(downloadType) {
    if (downloadInProgressRef.current) return;
    downloadInProgressRef.current = true;
    setDownloadInProgress(true);
    let filters = values;
    let requestParams = {
      page: 1,
      limit: totalNumber,
      search: '',
      mallName: filters.mallName,
      firstBillUploadDateStart: filters.firstBillUploadDateStart,
      firstBillUploadDateEnd: filters.firstBillUploadDateEnd,
      membershipType: filters.membershipType,
      download: true,
      downloadType,
    };

    try {
      const response = await getAllTotalPointsReportList(requestParams);
      const jobId = response?.data?.jobId;
      if (response?.responseCode !== 200 || !jobId) {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        downloadInProgressRef.current = false;
        setDownloadInProgress(false);
        return;
      }

      Toast.success('Report generation started');

      startExportPoll(jobId, {
        onDone: (url) => {
          triggerDownload(url);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onFailed: (error) => {
          Toast.error(error || Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onError: () => {
          Toast.error(Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
      });
    } catch (error) {
      console.error('startDownload failed:', error);
      Toast.error(Message.Error.COMMON_MESSAGE);
      downloadInProgressRef.current = false;
      setDownloadInProgress(false);
    }
  }

  async function handleDownloadcsv() {
    return startDownload('csv');
  }

  async function handleDownloadexcel() {
    return startDownload('excel');
  }

  return {
    pageLoading,
    filteredData,
    fetchReportData,
    navigate,
    totalNumber,
    currentPage,
    dataPerPage,
    pageCount,
    sectionObj,
    isFilterOpen,
    setIsFilterOpen,
    values,
    setValues,
    resetfilterModel,
    handleSubmit,
    handleFilterCancel,
    isFiltering,
  };
};

export default useOverAllPointsReport;
